
import React, { useState, useEffect } from "react";
import TableToolbar from "./components/TableToolbar";
import TableHeader from "./components/TableHeader";
import TableBody from "./components/TableBody";
import ContextMenu from "./components/ContextMenu";
import UploadStatus from "./components/UploadStatus";
import TableInfoBar from "./components/TableInfoBar";
import { useTableState } from "./hooks/useTableState";
import { useExcelOperations } from "./hooks/useExcelOperations";
import { useClipboardOperations } from "./hooks/useClipboardOperations";
import { useTableOperations } from "./hooks/useTableOperations";
import { tableStyles } from "./styles/tableStyles";

const EnhancedFinancialTable = ({
  financialData = [],
  onDataUpdate,
  isLoading = false,
  initialColumns = [
    { id: "particular", label: "Particular", width: 180 },
    { id: "year2022", label: "FY 2022", width: 120 },
    { id: "year2023", label: "FY 2023", width: 120 },
    { id: "year2024", label: "FY 2024", width: 120 }
  ],
  statementId = null,
  companyName = "",
  activeStatement = "Income Statement",
  onRefreshData
}) => {
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Initialize all hooks
  const tableState = useTableState({ 
    financialData, 
    initialColumns, 
    statementId,
    companyName,
    activeStatement,
    onDataUpdate
  });

  const excelOps = useExcelOperations({
    statementId,
    companyName,
    activeStatement,
    rows: tableState.rows,
    columns: tableState.columns,
    setEditHistory: tableState.setEditHistory,
    showNotification: tableState.showNotification,
    setRows: tableState.setRows,
    setColumns: tableState.setColumns,
    onDataUpdate
  });

  const clipboardOps = useClipboardOperations({
    rows: tableState.rows,
    columns: tableState.columns,
    selectedRange: tableState.selectedRange,
    clipboardData: tableState.clipboardData,
    setRows: tableState.setRows,
    setClipboardData: tableState.setClipboardData,
    setPendingChanges: tableState.setPendingChanges,
    setEditHistory: tableState.setEditHistory,
    onDataUpdate: tableState.onDataUpdate,
    showNotification: tableState.showNotification
  });

  const tableOps = useTableOperations({
    statementId,
    rows: tableState.rows,
    columns: tableState.columns,
    setRows: tableState.setRows,
    setColumns: tableState.setColumns,
    setPendingChanges: tableState.setPendingChanges,
    setEditHistory: tableState.setEditHistory,
    onDataUpdate: tableState.onDataUpdate,
    showNotification: tableState.showNotification,
    getColumnIndex: tableState.getColumnIndex,
    getColumnLetter: tableState.getColumnLetter
  });

  // Handle manual save
  const handleSaveChanges = async () => {
    if (!statementId) {
      tableState.showNotification('No statement selected', 'error');
      return;
    }
    
    if (tableState.pendingChanges.length === 0) {
      tableState.showNotification('No changes to save', 'info');
      return;
    }
    
    const result = await tableState.handleManualSave();
    
    if (result.success) {
      // Optionally refresh data after save
      if (onRefreshData) {
        onRefreshData();
      }
    }
  };

  // Handle upload
  const handleUpload = () => {
    excelOps.handleUploadClick();
  };

  // Combine all handlers - USE CONSISTENT NAMES
  const handlers = {
    // Excel operations
    handleDownloadOriginalExcel: excelOps.handleDownloadOriginalExcel,
    handleDownloadCurrentTable: excelOps.handleDownloadCurrentTable,
    handleUploadClick: handleUpload,
    handleFileUpload: excelOps.handleFileUpload,
    handleManualRefresh: () => {
      if (onRefreshData && typeof onRefreshData === 'function') {
        onRefreshData();
      } else {
        excelOps.refreshTableDataImmediately?.();
      }
    },
    
    // Clipboard operations - USE THE FUNCTIONS FROM clipboardOps
    handleCopyCell: clipboardOps.copyCell,
    handleCutCell: clipboardOps.cutCell,
    handlePasteCell: clipboardOps.pasteCell,
    handleCopySelection: clipboardOps.copySelection,
    handleCutSelection: clipboardOps.cutSelection,
    handlePasteToSelection: clipboardOps.pasteToSelection,
    
    // Table operations
    insertRowAbove: tableOps.insertRowAbove,
    insertRowBelow: tableOps.insertRowBelow,
    deleteRow: tableOps.deleteRow,
    insertColumn: tableOps.insertColumn,
    deleteColumn: tableOps.deleteColumn,
    
    // Table state operations
    handleManualSave: handleSaveChanges,
    clearSelection: tableState.clearSelection,
    startEditingCell: tableState.startEditingCell,
    onColumnResizeStart: tableState.handleColumnResizeStart,
    resetColumnWidth: tableState.resetColumnWidth
  };

  // Error handling effect
  useEffect(() => {
    if (financialData && financialData.length === 0 && !isLoading) {
      setHasError(true);
      setErrorMessage("No data available. The Excel file may be empty or corrupted.");
    } else if (tableState.rows.length === 0 && !isLoading) {
      setHasError(true);
      setErrorMessage("Failed to load table data. Please check the Excel file format.");
    } else {
      setHasError(false);
      setErrorMessage("");
    }
  }, [financialData, tableState.rows, isLoading]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center p-12">
        <div className="h-12 w-12 text-blue-500 animate-spin mb-4 rounded-full border-b-2 border-blue-500" />
        <p className="text-gray-600">Loading financial data...</p>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="flex flex-col items-center justify-center p-12 bg-gray-50 rounded-lg">
        <div className="text-red-500 text-4xl mb-4">⚠️</div>
        <h3 className="text-lg font-semibold text-gray-800 mb-2">
          {activeStatement} Data Unavailable
        </h3>
        <p className="text-gray-600 mb-4 text-center max-w-md">
          {errorMessage || "Excel file exists but data could not be loaded."}
        </p>
        <div className="flex gap-4">
          <button
            onClick={() => {
              if (onRefreshData) {
                onRefreshData();
              } else if (excelOps.refreshTableDataImmediately) {
                excelOps.refreshTableDataImmediately();
              }
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Refresh Data
          </button>
          <button
            onClick={() => excelOps.handleDownloadOriginalExcel()}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            disabled={!statementId}
          >
            Download Excel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative" ref={tableState.tableRef}>
      {/* Floating Action Buttons */}
      <TableToolbar
        pendingChanges={tableState.pendingChanges.length}
        isSaving={tableState.isSaving}
        isDownloading={excelOps.isDownloading}
        isUploading={excelOps.isUploading}
        isRefreshingData={excelOps.isRefreshingData}
        statementId={statementId}
        onSave={handlers.handleManualSave}
        onDownloadOriginal={handlers.handleDownloadOriginalExcel}
        onUpload={handlers.handleUploadClick}
        onRefresh={handlers.handleManualRefresh}
        fileInputRef={excelOps.fileInputRef}
      />

      {/* Table Info Bar */}
      <TableInfoBar
        companyName={companyName}
        activeStatement={activeStatement}
        rowsCount={tableState.rows.length}
        columnsCount={tableState.columns.length}
        pendingChanges={tableState.pendingChanges.length}
        lastSaveTime={tableState.lastSaveTime}
      />

      {/* Upload Status Display */}
      <UploadStatus
        uploadError={excelOps.uploadError}
        uploadSuccess={excelOps.uploadSuccess}
        uploadMessage={excelOps.uploadMessage}
        isUploading={excelOps.isUploading}
        uploadProgress={excelOps.uploadProgress}
        setUploadError={excelOps.setUploadError}
      />

      {/* Context Menu */}
      <ContextMenu
        contextMenu={tableState.contextMenu}
        selectedRange={tableState.selectedRange}
        clipboardData={tableState.clipboardData}
        statementId={statementId}
        rows={tableState.rows}
        columns={tableState.columns}
        getColumnIndex={tableState.getColumnIndex}
        getColumnLetter={tableState.getColumnLetter}
        onClose={tableState.closeContextMenu}
        handlers={handlers}
        showNotification={tableState.showNotification}
        contextMenuRef={tableState.contextMenuRef}
      />

      {/* Table */}
      <div 
        className="excel-wrapper" 
        onMouseUp={tableState.handleCellMouseUp}
        style={tableStyles.wrapper}
        ref={tableState.tableWrapperRef}
      >
        {tableState.rows.length > 0 ? (
          <table className="excel-table" style={tableStyles.table}>
            <thead>
              <TableHeader
                columns={tableState.columns}
                editingHeader={tableState.editingHeader}
                editValue={tableState.editValue}
                setEditValue={tableState.setEditValue}
                setEditingHeader={tableState.setEditingHeader}
                finishEditing={tableState.finishEditing}
                onColumnResizeStart={handlers.onColumnResizeStart}
              />
            </thead>
            <tbody>
              <TableBody
                rows={tableState.rows}
                columns={tableState.columns}
                editingCell={tableState.editingCell}
                editValue={tableState.editValue}
                selectedRange={tableState.selectedRange}
                onCellMouseDown={tableState.handleCellMouseDown}
                onCellMouseEnter={tableState.handleCellMouseEnter}
                onCellDoubleClick={handlers.startEditingCell}
                onContextMenu={tableState.handleContextMenu}
                setEditValue={tableState.setEditValue}
                finishEditing={tableState.finishEditing}
                formatValue={tableState.formatValue}
                shouldBeBold={tableState.shouldBeBold}
              />
            </tbody>
          </table>
        ) : (
          <div className="flex items-center justify-center h-64 text-gray-500">
            <div className="text-center">
              <div className="text-2xl mb-2">📊</div>
              <p>No table data available</p>
              <button
                onClick={handlers.handleManualRefresh}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Refresh Data
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Saving Indicator */}
      {tableState.isSaving && (
        <div style={tableStyles.savingIndicator}>
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
          Saving changes to server...
        </div>
      )}

      {/* Hidden file input for upload */}
      <input
        type="file"
        ref={excelOps.fileInputRef}
        className="hidden"
        accept=".xlsx,.xls"
        onChange={excelOps.handleFileUpload}
        key={`file-input-${statementId}`}
      />

      {/* Inline Styles */}
      <style>{tableStyles.inlineStyles}</style>
    </div>
  );
};

export default EnhancedFinancialTable;
